export enum RichTextType {
  DocumentType = "doc",
  ParagraphType = "paragraph",
  HeadingType = "heading",
  HorizontalRuleType = "horizontalRule",
  CodeBlockType = "codeBlock",
  ListType = "list",
  ListItemType = "listItem",
  BlockquoteType = "blockquote",
  TableType = "table",
  TableRowType = "tableRow",
  TableHeaderType = "tableHeader",
  TableCellType = "tableCell",
  BoldType = "bold",
  ItalicType = "italic",
  StrikeType = "strike",
  CodeType = "code",
  LinkType = "link",
  TextType = "text",
  ImageType = "image",
  MustacheVariableType = "variable:v1",
  CitationType = "citation",
}

export enum PmNodeType {
  Doc = "doc",
  Paragraph = "paragraph",
  Heading = "heading",
  HorizontalRule = "horizontalRule",
  CodeBlock = "codeBlock",
  BulletList = "bulletList",
  OrderedList = "orderedList",
  ListItem = "listItem",
  Blockquote = "blockquote",
  Text = "text",
  VariableV1 = "variable:v1",
  Table = "table",
  TableRow = "tableRow",
  TableCell = "tableCell",
  TableHeader = "tableHeader",
  Citation = "citation",
}

export enum PmMarkType {
  Bold = "bold",
  Italic = "italic",
  Strike = "strikethrough",
  Code = "code",
  Link = "link",
}

type PmBoldMark = {
  type: PmMarkType.Bold;
  attrs?: Record<string, unknown>;
};
type PmItalicMark = {
  type: PmMarkType.Italic;
  attrs?: Record<string, unknown>;
};
type PmStrikethroughMark = {
  type: PmMarkType.Strike;
  attrs?: Record<string, unknown>;
};
type PmCodeMark = {
  type: PmMarkType.Code;
  attrs?: Record<string, unknown>;
};
type PmLinkMark = {
  type: PmMarkType.Link;
  attrs: {
    href: string;
  } & Record<string, unknown>;
};
export type PmMark = PmBoldMark | PmItalicMark | PmStrikethroughMark | PmCodeMark | PmLinkMark;
export type PmText = {
  type: PmNodeType.Text;
  text: string;
  marks?: PmMark[];
};
export type PmCitation = {
  type: PmNodeType.Citation;
  attrs: {
    value: string;
  };
};
export type PmInline = PmText | PmVariable | PmCitation;
export type PmParagraph = {
  type: PmNodeType.Paragraph;
  content: PmInline[];
  attrs?: Record<string, unknown>;
};
export type PmHeading = {
  type: PmNodeType.Heading;
  attrs: {
    level: 1 | 2 | 3 | 4 | 5 | 6;
  };
  content: PmInline[];
};
export type PmBulletList = {
  type: PmNodeType.BulletList;
  content: PmListItem[];
};
export type PmOrderedList = {
  type: PmNodeType.OrderedList;
  content: PmListItem[];
};
export type PmListItem = {
  type: PmNodeType.ListItem;
  content: PmNode[];
};
export type PmBlockquote = {
  type: PmNodeType.Blockquote;
  content: PmNode[];
};
export type PmCodeBlock = {
  type: PmNodeType.CodeBlock;
  attrs?: {
    language?: string;
  };
  content: PmText[];
};

export const VariableKinds = ["asset", "concept", "label", "view"] as const;

/**
 * Utility type for ensuring exhaustive checking of VariableKinds.
 * Use this in switch statements to ensure all variable types are handled.
 *
 * Example:
 * ```typescript
 * const handleVariableType = (type: VariableKind): string => {
 *   switch (type) {
 *     case "asset": return "Asset";
 *     case "concept": return "Concept";
 *     case "label": return "Label";
 *     case "view": return "View";
 *     default: {
 *       // This will cause a TypeScript error if any VariableKind is missing
 *       const _exhaustive: never = type;
 *       throw new Error(`Unhandled variable type: ${_exhaustive}`);
 *     }
 *   }
 * };
 * ```
 */
export type VariableKind = (typeof VariableKinds)[number];

/**
 * Runtime utility to assert exhaustive checking of VariableKinds.
 * Throws an error if called, ensuring all cases are handled at compile time.
 */
export function assertExhaustive(value: never) {
  console.error(`Exhaustive check failed with value: ${value}`);
}

export type PmVariable = {
  type: PmNodeType.VariableV1;
  attrs: {
    type: VariableKind;
    id: string;
    text: string;
  };
};
export type PmTable = {
  type: PmNodeType.Table;
  content: PmTableRow[];
};
export type PmTableRow = {
  type: PmNodeType.TableRow;
  content: PmTableCell[] | PmTableHeader[];
};
export type PmTableCell = {
  type: PmNodeType.TableCell;
  attrs?: {
    colspan?: number;
    rowspan?: number;
    alignment?: string;
  };
  content: PmNode[];
};

export type PmTableHeader = Omit<PmTableCell, "type"> & {
  type: PmNodeType.TableHeader;
};

export type PmHorizontalRule = {
  type: PmNodeType.HorizontalRule;
  attrs?: {
    style?: string;
  };
};
export type PmNode =
  | PmParagraph
  | PmHeading
  | PmBulletList
  | PmOrderedList
  | PmListItem
  | PmBlockquote
  | PmCodeBlock
  | PmTable
  | PmTableRow
  | PmTableCell
  | PmHorizontalRule
  | PmVariable;

export type PmDoc = {
  type: PmNodeType.Doc;
  content: PmNode[];
};
